import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  STUDIO_INSTALL_DISMISS_KEY,
  usePwaInstall,
} from '../../pwa/usePwaInstall';
import { resolveStudioInstallPlatform } from '../utils/studioInstallPlatform';
import StudioMobileAppBanner from './StudioMobileAppBanner';
import StudioMobileAppModal from './StudioMobileAppModal';
import StudioToast from './StudioToast';
import { STUDIO_COPY } from '../config';

type StudioMobileAppContextValue = {
  openInstructions: () => void;
  isStandalone: boolean;
  isMobileWeb: boolean;
  isDismissed: boolean;
  dismissBanner: () => void;
};

const StudioMobileAppContext = createContext<StudioMobileAppContextValue | null>(
  null
);

export function useStudioMobileApp(): StudioMobileAppContextValue | null {
  return useContext(StudioMobileAppContext);
}

export const StudioMobileAppBannerHost: React.FC = () => {
  const ctx = useStudioMobileApp();
  if (!ctx) return null;
  return (
    <StudioMobileAppBanner
      isStandalone={ctx.isStandalone}
      isMobileWeb={ctx.isMobileWeb}
      isDismissed={ctx.isDismissed}
      onDismiss={ctx.dismissBanner}
      onOpen={ctx.openInstructions}
    />
  );
};

/**
 * Registers the single shared `beforeinstallprompt` listener via
 * `usePwaInstall` → `ensureInstallPromptListener`, and hosts the modal/toast.
 */
export const StudioMobileAppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const install = usePwaInstall({ dismissKey: STUDIO_INSTALL_DISMISS_KEY });
  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(true);

  useEffect(() => {
    setBannerDismissed(install.isInstallBannerDismissed());
  }, [install.isInstallBannerDismissed]);

  const openInstructions = useCallback(() => setModalOpen(true), []);
  const closeInstructions = useCallback(() => setModalOpen(false), []);

  const handleDismissBanner = useCallback(() => {
    install.dismissInstallBanner();
    setBannerDismissed(true);
  }, [install]);

  const platform = resolveStudioInstallPlatform(
    install.isIos,
    install.isIosSafari,
    install.canPromptInstall
  );

  const value = useMemo(
    () => ({
      openInstructions,
      isStandalone: install.isStandalone,
      isMobileWeb: install.isMobileWeb,
      isDismissed: bannerDismissed,
      dismissBanner: handleDismissBanner,
    }),
    [
      openInstructions,
      install.isStandalone,
      install.isMobileWeb,
      bannerDismissed,
      handleDismissBanner,
    ]
  );

  const handleInstalled = useCallback(() => {
    handleDismissBanner();
    setModalOpen(false);
    setToast(STUDIO_COPY.mobileAppInstalledToast);
  }, [handleDismissBanner]);

  return (
    <StudioMobileAppContext.Provider value={value}>
      {children}
      <StudioMobileAppModal
        open={modalOpen}
        onClose={closeInstructions}
        platform={platform}
        canPromptInstall={install.canPromptInstall}
        promptInstall={install.promptInstall}
        onInstalled={handleInstalled}
      />
      <StudioToast message={toast} onDismiss={() => setToast(null)} />
    </StudioMobileAppContext.Provider>
  );
};

export default StudioMobileAppProvider;
